import styled from 'styled-components';
import {
  buildColorWarningKey,
  buildNumberWarningKey,
  ComponentOrReferenceFormattingRule,
  FormattingColorPicker,
  ColorPickerWrapper,
  ValueWarningMessage,
  getReversedRangeWarning,
  type Formatting,
  type FormattingContextState,
} from '@ardoq/perspectives';
import {
  isNumberPairComparator,
  QueryBuilderFilterType,
  RuleValueInput,
} from '@ardoq/query-builder';
import { space } from '@ardoq/design-tokens';
import { HelperTextWrapper } from '@ardoq/forms';
import { RowHeader } from '../../../atoms';
import { BottomRowWrapper } from './atoms';
import { FlexBox } from '@ardoq/layout';

const getValuePlaceholder = (filterType: QueryBuilderFilterType) => {
  switch (filterType) {
    case QueryBuilderFilterType.ASYNC_COMPONENT_OID:
    case QueryBuilderFilterType.ASYNC_REFERENCE_OID:
      return 'Search and select an Ardoq OID';
    case QueryBuilderFilterType.ASYNC_PARENT:
    case QueryBuilderFilterType.ASYNC_SOURCE:
    case QueryBuilderFilterType.ASYNC_TARGET:
    case QueryBuilderFilterType.ASYNC_ROOT_WORKSPACE:
      return 'Search and select a component';
    case QueryBuilderFilterType.ASYNC_USER:
      return 'Search and select a user';
    case QueryBuilderFilterType.NUMBER:
      return 'Type a number value';
    case QueryBuilderFilterType.SELECT_MULTIPLE:
      return 'Search and select options';
  }
  return undefined;
};

const FormattingRuleRowContainer = styled(BottomRowWrapper)`
  gap: ${space.small};
`;

type FormattingRuleSingleRowColorAndValueEditorsProperties = {
  rule: ComponentOrReferenceFormattingRule;
  filter: Formatting;
  formattingContext: FormattingContextState;
  colorPaletteAlignment?: 'left' | 'right';
};
const FormattingRuleSingleRowColorAndValueEditors = ({
  rule,
  filter,
  formattingContext,
  colorPaletteAlignment,
}: FormattingRuleSingleRowColorAndValueEditorsProperties) => {
  const { __id: ruleId, value, field, comparator, color } = rule;
  const { filterType } = filter;
  const {
    asyncSuggestionsLoaders,
    asyncLabelLoaders,
    updateRule,
    ruleInfo,
    i18nLocale,
  } = formattingContext;
  const { colorWarnings = {}, numberWarnings = {} } = ruleInfo[rule.__id] || {};
  const colorWarning = colorWarnings[buildColorWarningKey(rule.color)];

  // Build number warning key only for number rules
  const numberWarning =
    field && filterType === QueryBuilderFilterType.NUMBER
      ? numberWarnings[buildNumberWarningKey(field, comparator, value)]
      : undefined;

  const valuePlaceholder = getValuePlaceholder(filterType);
  const helperText =
    filterType === QueryBuilderFilterType.NUMBER
      ? isNumberPairComparator(comparator)
        ? 'Decimals can be typed in. Default values are the minimum and maximum in your dataset.'
        : 'Decimals can be typed in.'
      : null;

  const reversedRangeWarning = getReversedRangeWarning({
    i18nLocale,
    filterType,
    comparator,
    value,
  });
  const valueWarning = reversedRangeWarning ?? numberWarning;

  const hasValueInputWarning = Boolean(colorWarning) || Boolean(valueWarning);

  return (
    <>
      <RowHeader>Value</RowHeader>
      {helperText && <HelperTextWrapper>{helperText}</HelperTextWrapper>}
      <FormattingRuleRowContainer>
        <ColorPickerWrapper $hasWarning={Boolean(colorWarning)}>
          <FormattingColorPicker
            dataTestId="formatting-rule-color-picker"
            value={color}
            onValueChange={color => updateRule(ruleId, { color })}
            isNewConditionalFormatting={true}
            colorPaletteAlignment={colorPaletteAlignment}
          />
        </ColorPickerWrapper>
        {filterType && comparator && (
          <FlexBox
            gap="xsmall"
            flex={1}
            style={{ minWidth: 0, flexWrap: 'wrap' }}
          >
            <RuleValueInput
              key={field}
              operator={comparator}
              asyncSuggestionsLoaders={asyncSuggestionsLoaders}
              asyncLabelLoaders={asyncLabelLoaders}
              filterType={filterType}
              filter={filter}
              placeholder={valuePlaceholder}
              // Formatting rules fallback to null, but null is not a valid value for this input type. undefined is.
              value={value ?? undefined}
              onValueChange={value =>
                updateRule(ruleId, {
                  value: Number.isNaN(value) ? null : value,
                })
              }
              hasWarning={hasValueInputWarning}
              hideWarningIcon={!hasValueInputWarning}
              locale={formattingContext.locale}
              isNewConditionalFormatting={true}
              menuPortalTarget={null}
            />
            <ValueWarningMessage message={valueWarning} />
          </FlexBox>
        )}
      </FormattingRuleRowContainer>
    </>
  );
};
export { FormattingRuleSingleRowColorAndValueEditors };
